import type { SpotDto } from "@boomap/contracts";
import {
  GeoJSONSource,
  type GeoJSONSourceSpecification,
  type LayerSpecification,
  type LngLatBoundsLike,
  type LngLatLike,
  Map as MapLibreMap,
  type MapLayerMouseEvent,
  type MapMouseEvent,
  Marker,
  NavigationControl,
  ScaleControl,
} from "maplibre-gl";
import { useEffect, useImperativeHandle, useRef } from "react";

import type { GeoPoint, MapArea, MapFocus } from "../lib/types.ts";
import {
  clusterCountLayer,
  clusterLayer,
  clusterRingLayer,
  pointLayer,
  SPOT_LAYERS,
  SPOTS_SOURCE_ID,
} from "./layers.ts";
import { BASEMAP_ATTRIBUTION, type BasemapId, BASEMAPS } from "./map-style.ts";
import { loadSpotIcons, registerSpotIcons, type SpotIconImages } from "./spot-icons.ts";
import { EMPTY_COLLECTION, toSpotCollection } from "./to-geojson.ts";
// Effet de bord voulu : désigne le worker de MapLibre avant toute carte.
import "./worker.ts";

/** Couches des pastilles, de la plus basse à la plus haute. */
const SPOT_LAYER_SPECS: readonly LayerSpecification[] = [
  clusterRingLayer,
  clusterLayer,
  clusterCountLayer,
  pointLayer,
];

const spotsSource = (spots: readonly SpotDto[]): GeoJSONSourceSpecification => ({
  type: "geojson",
  data: spots.length === 0 ? EMPTY_COLLECTION : toSpotCollection(spots),
  cluster: true,
  clusterRadius: 44,
  clusterMaxZoom: 16,
});

/**
 * (Ré)installe la source, les couches et les images des pastilles sur le style
 * courant. Idempotent : sûre à appeler après un changement de fond de carte,
 * que `transformStyle` ait déjà replacé les couches ou non.
 */
const installSpotLayers = (
  map: MapLibreMap,
  icons: SpotIconImages,
  spots: readonly SpotDto[],
): void => {
  registerSpotIcons(map, icons);

  if (map.getSource(SPOTS_SOURCE_ID) === undefined) {
    map.addSource(SPOTS_SOURCE_ID, spotsSource(spots));
  }

  for (const layer of SPOT_LAYER_SPECS) {
    if (map.getLayer(layer.id) === undefined) {
      map.addLayer(layer);
    }
  }
};

/**
 * France métropolitaine et Corse, au plus juste : de Ouessant à la Corse, de
 * Bonifacio à Bray-Dunes. Le service ne couvre que la France, et `maxBounds`
 * interdit à la fenêtre d'en sortir — ce qui borne aussi le dézoom, sans
 * `minZoom` à régler pour chaque format d'écran.
 */
const FRANCE_BOUNDS: LngLatBoundsLike = [
  [-5.2, 41.3],
  [9.6, 51.1],
];

/**
 * Vue d'arrivée : centrée sur Paris, un peu plus près que le pays entier
 * (2^1,5 ≈ 2,8 fois). Assez large pour rester une vue d'ensemble — la
 * géolocalisation n'est demandée qu'au clic — et à cette échelle l'API répond
 * `zoomedOut`, le bandeau invitant à zoomer ou chercher une ville.
 */
const INITIAL_ZOOM_BOOST = 1.5;
const INITIAL_CENTER: LngLatLike = [2.3522, 48.8566];

/** Marges de cadrage : la barre du haut et la feuille du bas mangent l'écran. */
const FIT_PADDING = { top: 130, bottom: 200, left: 40, right: 40 } as const;

/**
 * Remontée du réticule de placement par rapport au centre du conteneur, pour
 * qu'il tombe au milieu de la zone réellement visible entre la barre du haut et
 * la barre de confirmation du bas.
 *
 * La même constante positionne l'élément et sert au calcul de `unproject` : un
 * décalage exprimé deux fois dériverait, et le repère ne désignerait plus le
 * point annoncé.
 */
const CROSSHAIR_OFFSET_IN_PIXELS = 46;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Fenêtre visible, ramenée dans des bornes valides.
 *
 * Très dézoomée, MapLibre rend des longitudes au-delà de ±180 (plusieurs
 * mondes côte à côte). Limitation connue et assumée : une vue qui franchit
 * l'antiméridien est traitée comme le monde entier, donc renvoyée comme
 * « dézoomée » plutôt que découpée en deux requêtes.
 */
const visibleArea = (map: MapLibreMap): MapArea => {
  const bounds = map.getBounds();
  const west = clamp(bounds.getWest(), -180, 180);
  const east = clamp(bounds.getEast(), -180, 180);
  const crossesAntimeridian = west >= east;

  return {
    south: clamp(bounds.getSouth(), -90, 89.9),
    north: clamp(bounds.getNorth(), -89.9, 90),
    west: crossesAntimeridian ? -180 : west,
    east: crossesAntimeridian ? 180 : east,
  };
};

export interface MapViewHandle {
  /**
   * Point actuellement visé par le réticule. Lu au moment de la confirmation
   * plutôt que remonté à chaque `move` : la valeur reste juste même pendant
   * l'inertie, et l'application ne rend pas soixante fois par seconde.
   */
  readonly placementTarget: () => GeoPoint;
}

export interface MapViewProps {
  readonly basemap: BasemapId;
  readonly spots: readonly SpotDto[];
  readonly pendingLocation: GeoPoint | null;
  /** En mode placement, la carte affiche un réticule et absorbe les clics. */
  readonly placing: boolean;
  /** Libellé accessible du conteneur, traduit par l'appelant. */
  readonly mapLabel: string;
  readonly onAreaChange: (area: MapArea) => void;
  readonly onSpotClick: (spotId: string) => void;
  readonly focus: MapFocus | null;
  readonly ref?: React.Ref<MapViewHandle>;
}

/**
 * Enveloppe React autour de MapLibre. La carte est un objet impératif à durée de
 * vie longue : React ne pilote que ses entrées (données, cadrage) et ses sorties
 * (événements), jamais son rendu.
 */
export const MapView = ({ ref, ...props }: MapViewProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const pendingMarkerRef = useRef<Marker | null>(null);
  const iconsRef = useRef<SpotIconImages | null>(null);
  const appliedBasemapRef = useRef<BasemapId>(props.basemap);

  // `setStyle` remet le style à zéro : la source, les couches et les images des
  // pastilles disparaissent avec l'ancien fond. Les données courantes sont donc
  // gardées ici pour être réinjectées après chaque bascule.
  const spotsRef = useRef<readonly SpotDto[]>(props.spots);

  // Les callbacks changent à chaque rendu ; les garder dans une ref évite de
  // détruire et recréer la carte à chaque fois. L'affectation se fait dans un
  // effet, pas pendant le rendu.
  const handlersRef = useRef(props);

  useEffect(() => {
    handlersRef.current = props;
  });

  useEffect(() => {
    const container = containerRef.current;

    if (container === null) {
      return undefined;
    }

    const map = new MapLibreMap({
      container,
      style: BASEMAPS[appliedBasemapRef.current].url,
      bounds: FRANCE_BOUNDS,
      maxZoom: 19,
      maxBounds: FRANCE_BOUNDS,
      attributionControl: { compact: true, customAttribution: BASEMAP_ATTRIBUTION },
    });

    // Le zoom de départ se déduit de l'emprise, déjà ajustée au format de l'écran.
    map.jumpTo({ center: INITIAL_CENTER, zoom: map.getZoom() + INITIAL_ZOOM_BOOST });

    mapRef.current = map;
    let disposed = false;

    map.addControl(new NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");

    const reportArea = (): void => {
      handlersRef.current.onAreaChange(visibleArea(map));
    };

    map.on("load", () => {
      // Les icônes sont rastérisées avant d'ajouter la couche qui les utilise,
      // sinon MapLibre journalise une image manquante par pastille.
      void (async () => {
        const icons = await loadSpotIcons();

        if (disposed) {
          return;
        }

        iconsRef.current = icons;
        installSpotLayers(map, icons, spotsRef.current);
        reportArea();
      })();
    });

    map.on("moveend", reportArea);

    // Un clic sur un agrégat zoome dessus : c'est la seule façon d'en sortir
    // sans compteur textuel.
    map.on("click", SPOT_LAYERS.clusters, (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];

      if (feature === undefined || feature.geometry.type !== "Point") {
        return;
      }

      const [longitude, latitude] = feature.geometry.coordinates;

      if (longitude === undefined || latitude === undefined) {
        return;
      }

      map.easeTo({
        center: [longitude, latitude],
        zoom: Math.min(map.getZoom() + 2.5, 18),
      });
    });

    map.on("click", SPOT_LAYERS.points, (event: MapLayerMouseEvent) => {
      const id = event.features?.[0]?.properties?.["id"];

      // En mode placement, un clic recentre : il ne doit pas ouvrir une pastille.
      if (typeof id === "string" && !handlersRef.current.placing) {
        handlersRef.current.onSpotClick(id);
      }
    });

    /**
     * Hors mode placement, un clic sur le fond ne fait rien. C'est délibéré :
     * toucher la carte est le geste de navigation, l'y greffer une création
     * ouvrait une feuille modale à chaque tap involontaire.
     */
    map.on("click", (event: MapMouseEvent) => {
      if (handlersRef.current.placing) {
        map.easeTo({ center: event.lngLat, duration: 350 });
      }
    });

    for (const layer of [SPOT_LAYERS.points, SPOT_LAYERS.clusters]) {
      map.on("mouseenter", layer, () => {
        map.getCanvas().style.cursor = handlersRef.current.placing ? "" : "pointer";
      });
      map.on("mouseleave", layer, () => {
        map.getCanvas().style.cursor = "";
      });
    }

    return () => {
      disposed = true;
      pendingMarkerRef.current?.remove();
      pendingMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    spotsRef.current = props.spots;

    const map = mapRef.current;
    const source = map?.getSource(SPOTS_SOURCE_ID);

    if (source instanceof GeoJSONSource) {
      // `setData` est asynchrone depuis MapLibre 6 : un échec de mise à jour ne
      // doit pas remonter en rejet non géré.
      source.setData(toSpotCollection(props.spots)).catch((error: unknown) => {
        console.error("Mise à jour des pastilles impossible", error);
      });
    }
  }, [props.spots]);

  useEffect(() => {
    const map = mapRef.current;

    if (map === null) {
      return;
    }

    if (props.pendingLocation === null) {
      pendingMarkerRef.current?.remove();
      pendingMarkerRef.current = null;
      return;
    }

    const position: LngLatLike = [props.pendingLocation.longitude, props.pendingLocation.latitude];

    if (pendingMarkerRef.current === null) {
      const element = document.createElement("div");
      element.className = "pending-marker";
      element.setAttribute("aria-hidden", "true");
      pendingMarkerRef.current = new Marker({ element, anchor: "center" })
        .setLngLat(position)
        .addTo(map);
    } else {
      pendingMarkerRef.current.setLngLat(position);
    }
  }, [props.pendingLocation]);

  useEffect(() => {
    const map = mapRef.current;
    const icons = iconsRef.current;

    if (map === null || props.basemap === appliedBasemapRef.current) {
      return;
    }

    appliedBasemapRef.current = props.basemap;

    // `transformStyle` est le point d'accroche prévu par MapLibre : nos couches
    // entrent dans le nouveau style au moment où il est posé, sans fenêtre où la
    // carte serait sans pastilles.
    //
    // Ne pas essayer d'attendre `isStyleLoaded()` sur `styledata` : après un
    // `setStyle`, il reste faux tant que les tuiles chargent, et l'événement
    // cesse d'être émis avant — les couches ne revenaient jamais.
    map.setStyle(BASEMAPS[props.basemap].url, {
      diff: false,
      transformStyle: (_previous, next) => ({
        ...next,
        sources: { ...next.sources, [SPOTS_SOURCE_ID]: spotsSource(spotsRef.current) },
        layers: [...next.layers, ...SPOT_LAYER_SPECS],
      }),
    });

    // Les images ajoutées par `addImage`, elles, ne survivent pas au changement
    // de style : il faut les réenregistrer une fois le nouveau style en place.
    map.once("styledata", () => {
      if (icons !== null) {
        installSpotLayers(map, icons, spotsRef.current);
      }
    });
  }, [props.basemap]);

  useEffect(() => {
    const map = mapRef.current;
    const focus = props.focus;

    if (map === null || focus === null) {
      return;
    }

    if (focus.kind === "area") {
      map.fitBounds(
        [
          [focus.west, focus.south],
          [focus.east, focus.north],
        ],
        { padding: FIT_PADDING, maxZoom: 17, duration: 900 },
      );

      return;
    }

    map.flyTo({
      center: [focus.longitude, focus.latitude],
      zoom: focus.zoom ?? 16,
      speed: 1.4,
    });
  }, [props.focus]);

  useImperativeHandle(
    ref,
    () => ({
      placementTarget: () => {
        const map = mapRef.current;

        if (map === null) {
          return { latitude: 0, longitude: 0 };
        }

        const canvas = map.getCanvas();
        const target = map.unproject([
          canvas.clientWidth / 2,
          canvas.clientHeight / 2 - CROSSHAIR_OFFSET_IN_PIXELS,
        ]);

        return { latitude: target.lat, longitude: target.lng };
      },
    }),
    [],
  );

  return (
    <div className="map-shell">
      <div ref={containerRef} className="map" role="application" aria-label={props.mapLabel} />

      {props.placing && (
        <svg
          className="crosshair"
          style={{ top: `calc(50% - ${CROSSHAIR_OFFSET_IN_PIXELS.toString()}px)` }}
          viewBox="0 0 64 64"
          width="64"
          height="64"
          aria-hidden="true"
        >
          <g fill="none" stroke="#150b1f" strokeWidth="5" strokeLinecap="round">
            <circle cx="32" cy="32" r="13" />
            <path d="M32 6v9M32 49v9M6 32h9M49 32h9" />
          </g>
          <g fill="none" stroke="#ff8a3d" strokeWidth="2.6" strokeLinecap="round">
            <circle cx="32" cy="32" r="13" />
            <path d="M32 6v9M32 49v9M6 32h9M49 32h9" />
          </g>
          <circle cx="32" cy="32" r="3.2" fill="#ff8a3d" stroke="#150b1f" strokeWidth="1.6" />
        </svg>
      )}
    </div>
  );
};
